import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION_DF } from "oolib/dist/v2/components/Typo2";
import { ChartLine } from "@phosphor-icons/react";
import { TrendLineChart } from "@/_self_managed/SM_Dashboard/LensesV2/comps/TrendLineChart";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";

/**
 * The signal categories over time, one line each: the per-signal series
 * the Priorities scoring already fetches (Elasticsearch, per period at the
 * period's grain, over the history window), folded into categories here.
 * Plotted from the first period with any observations to the end of the
 * window, zero months in between included — the window itself reaches back
 * a fixed number of periods, which can be before the corpus starts. Sits in
 * the Overview V2 widget shell; `blurNote` (and its icon) pass through to it.
 * `singlePeriod` is the section's call that only one period has data — the
 * same flag that sets the note — and switches the lead-in below on.
 */
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
`;
// Zero periods kept ahead of a lone period with data (singlePeriod).
const LEAD_IN_PERIODS = 6;
const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`;

export const SignalCategoryTrend = ({ signalSeries, periodKeys, signalMetaById, grain, singlePeriod = false, blurNote, blurNoteIcon }) => {
  const categoryTheme = useSignalCategories();
  const data = useMemo(() => {
    const rows = periodKeys.map((key) => ({
      timeInterval: key,
      dimensions: categoryTheme.list.map((cat) => ({
        value: cat.value,
        display: cat.display,
        count: Object.entries(signalSeries).reduce((acc, [id, series]) => (signalMetaById[id]?.category === cat.value ? acc + (series[key] || 0) : acc), 0),
      })),
    }));
    const first = rows.findIndex((row) => row.dimensions.some((d) => d.count > 0));
    if (first < 0) return rows;
    // A single period with observations leaves nothing to draw a line
    // between, so keep some of the zero months before it then (not the
    // whole window — a year of empty axis reads as missing data, not as
    // a teaser): the widget sits blurred behind the more-data note and
    // should still show a line rising into its month.
    return rows.slice(singlePeriod ? Math.max(0, first - LEAD_IN_PERIODS) : first);
  }, [signalSeries, periodKeys, signalMetaById, categoryTheme, singlePeriod]);
  const colors = useMemo(() => Object.fromEntries(categoryTheme.list.map((cat) => [cat.display, categoryTheme.accentOf(cat.value)])), [categoryTheme]);

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <ChartLine size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Signal categories over time</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      style={{ height: "100%" }}
          blurNote={blurNote}
      blurNoteIcon={blurNoteIcon}
    >
      <TrendLineChart data={data} colors={colors} interval={grain} height={260} defaultOpacity={1} hideTopGridLine />
      <Legend style={{ marginTop: 12 }}>
        {categoryTheme.list.map((cat) => (
          <UI_CAPTION_DF key={cat.value} style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>
            <Dot $color={categoryTheme.accentOf(cat.value)} />
            {cat.display}
          </UI_CAPTION_DF>
        ))}
      </Legend>
    </DashboardWidgetShell>
  );
};
